import React, { useState, useRef } from "react";
import { periodShort, periodLabel, formatValue } from "../labels";

/**
 * Мини-график карточки (ТЗ п.5.5) — чистый SVG.
 *
 *  - линия факта: цвет по статусу отчётного периода, градиентная заливка;
 *  - линия плана: тонкий пунктир по точкам ряда;
 *  - оси X (короткие подписи периодов, автопропуск) и Y (шкала, 3 деления);
 *  - точки на всех периодах; точка report — увеличенный маркер, если в диапазоне;
 *  - тултип по оси X (режим index): полная подпись периода + Факт и План;
 *  - fact=null → разрыв линии (spanGaps=false).
 *
 * props: series[], statusColor, reportKey, lang, L, id
 */

const W = 300;
const H = 130;
const PAD = { top: 12, right: 12, bottom: 26, left: 42 };

const C = {
  grid: "rgba(148, 163, 184, 0.14)",
  plan: "rgba(160, 174, 196, 0.6)",
  axis: "#7c8aa5",
  tooltipBg: "#1a2740",
  tooltipBorder: "#2c3d5e",
};

const Sparkline = ({ series, statusColor, reportKey, lang, L, id }) => {
  const [hover, setHover] = useState(null);
  const svgRef = useRef(null);

  if (!series || series.length === 0) return null;

  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;

  const values = [];
  series.forEach((p) => {
    if (p.fact !== null && p.fact !== undefined) values.push(p.fact);
    if (p.plan !== null && p.plan !== undefined) values.push(p.plan);
  });
  let yMin = values.length ? Math.min(...values) : 0;
  let yMax = values.length ? Math.max(...values) : 1;
  if (yMin === yMax) {
    yMin -= 1;
    yMax += 1;
  } else {
    const pad = (yMax - yMin) * 0.15;
    yMin -= pad;
    yMax += pad;
  }
  const yMid = (yMin + yMax) / 2;

  const xAt = (i) =>
    series.length === 1
      ? PAD.left + innerW / 2
      : PAD.left + (i / (series.length - 1)) * innerW;
  const yAt = (v) => PAD.top + innerH - ((v - yMin) / (yMax - yMin)) * innerH;

  const brokenPath = (getVal) => {
    let d = "";
    let open = false;
    series.forEach((p, i) => {
      const v = getVal(p);
      if (v === null || v === undefined) {
        open = false;
        return;
      }
      d += `${open ? " L" : " M"} ${xAt(i).toFixed(1)} ${yAt(v).toFixed(1)}`;
      open = true;
    });
    return d.trim();
  };

  const factPath = brokenPath((p) => p.fact);
  const planPath = brokenPath((p) => p.plan);

  const areaSegments = [];
  let seg = [];
  series.forEach((p, i) => {
    if (p.fact === null || p.fact === undefined) {
      if (seg.length > 1) areaSegments.push(seg);
      seg = [];
    } else {
      seg.push({ x: xAt(i), y: yAt(p.fact) });
    }
  });
  if (seg.length > 1) areaSegments.push(seg);

  const areaPath = (s) =>
    `M ${s[0].x.toFixed(1)} ${s[0].y.toFixed(1)} ` +
    s
      .slice(1)
      .map((pt) => `L ${pt.x.toFixed(1)} ${pt.y.toFixed(1)}`)
      .join(" ") +
    ` L ${s[s.length - 1].x.toFixed(1)} ${PAD.top + innerH}` +
    ` L ${s[0].x.toFixed(1)} ${PAD.top + innerH} Z`;

  const maxLabels = Math.max(2, Math.floor(innerW / 34));
  const step = Math.ceil(series.length / maxLabels);
  const xTicks = series
    .map((p, i) => ({ p, i }))
    .filter(({ i }) => i % step === 0 || i === series.length - 1);

  const gridYs = [
    { y: PAD.top, v: yMax },
    { y: PAD.top + innerH / 2, v: yMid },
    { y: PAD.top + innerH, v: yMin },
  ];

  const gradId = `hc-grad-${id}`;

  const onMove = (e) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * W;
    let best = 0;
    let bestDist = Infinity;
    series.forEach((_, i) => {
      const d = Math.abs(xAt(i) - x);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    });
    setHover(best);
  };

  const hovered = hover !== null ? series[hover] : null;

  let tip = null;
  if (hovered) {
    const tw = 128;
    const th = 46;
    let tx = xAt(hover) + 10;
    if (tx + tw > W - 4) tx = xAt(hover) - tw - 10;
    const ty = Math.max(
      4,
      Math.min(
        H - th - 4,
        (hovered.fact !== null ? yAt(hovered.fact) : PAD.top + innerH / 2) -
          th / 2
      )
    );
    tip = { tx, ty, tw, th };
  }

  return (
    <svg
      ref={svgRef}
      className="hc-sparkline"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="xMidYMid meet"
      onMouseMove={onMove}
      onMouseLeave={() => setHover(null)}
      role="img"
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={statusColor} stopOpacity="0.32" />
          <stop offset="100%" stopColor={statusColor} stopOpacity="0.02" />
        </linearGradient>
      </defs>

      {gridYs.map((g, i) => (
        <g key={i}>
          <line
            x1={PAD.left}
            x2={W - PAD.right}
            y1={g.y}
            y2={g.y}
            stroke={C.grid}
          />
          <text
            x={PAD.left - 6}
            y={g.y + 3.5}
            textAnchor="end"
            className="hc-spark-axis"
          >
            {formatValue(g.v)}
          </text>
        </g>
      ))}

      {areaSegments.map((s, i) => (
        <path key={i} d={areaPath(s)} fill={`url(#${gradId})`} />
      ))}

      {planPath && (
        <path
          d={planPath}
          fill="none"
          stroke={C.plan}
          strokeWidth="1.2"
          strokeDasharray="5 4"
        />
      )}

      {factPath && (
        <path
          d={factPath}
          fill="none"
          stroke={statusColor}
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      )}

      {series.map((p, i) => {
        if (p.fact === null || p.fact === undefined) return null;
        const isReport = p.key === reportKey;
        return (
          <g key={p.key}>
            {isReport && (
              <circle
                cx={xAt(i)}
                cy={yAt(p.fact)}
                r="6.5"
                fill="none"
                stroke={statusColor}
                strokeOpacity="0.45"
                strokeWidth="2"
              />
            )}
            <circle
              cx={xAt(i)}
              cy={yAt(p.fact)}
              r={isReport ? 4 : 2.8}
              fill={statusColor}
            />
          </g>
        );
      })}

      {hovered && (
        <line
          x1={xAt(hover)}
          x2={xAt(hover)}
          y1={PAD.top}
          y2={PAD.top + innerH}
          stroke="rgba(148,163,184,0.35)"
          strokeDasharray="3 3"
        />
      )}

      {xTicks.map(({ p, i }) => (
        <text
          key={p.key}
          x={xAt(i)}
          y={H - 7}
          textAnchor="middle"
          className="hc-spark-axis"
        >
          {periodShort(p)}
        </text>
      ))}

      {hovered && tip && (
        <g pointerEvents="none">
          <rect
            x={tip.tx}
            y={tip.ty}
            width={tip.tw}
            height={tip.th}
            rx="6"
            fill={C.tooltipBg}
            stroke={C.tooltipBorder}
          />
          <text
            x={tip.tx + 8}
            y={tip.ty + 14}
            className="hc-spark-tip hc-spark-tip--title"
          >
            {periodLabel(hovered, lang)}
          </text>
          <text x={tip.tx + 8} y={tip.ty + 27} className="hc-spark-tip">
            {L.fact}: {formatValue(hovered.fact)}
          </text>
          <text x={tip.tx + 8} y={tip.ty + 40} className="hc-spark-tip">
            {L.plan}: {formatValue(hovered.plan)}
          </text>
        </g>
      )}
    </svg>
  );
};

export default Sparkline;
